import { Vector3 } from 'three';
import type { GameContext } from '../core/context';
import type { CrimeType } from '../core/events';
import { angleDelta, clamp, distance, type V2 } from '../core/math';
import type { System } from '../core/system';
import type { Body } from '../physics/body';
import { Layer } from '../physics/types';
import type { Vehicle } from '../vehicles/types';
import type { VehicleService } from '../vehicles/vehicleSystem';
import type { CameraRig } from '../camera/cameraRig';
import { DEFAULT_DRIVE_SHAPE, DriveShaper, type DriveShapeOptions } from '../input/driveShaping';
import { pickInteractable, type Interactable, type InteractionPrompt, type InteractionService } from './interactions';
import { ControlLock, RESPAWN_REASONS } from './controlLock';
import { MOVE_DEAD, aimTurnHeading, footSlewRate, footTargetSpeed, slewVelocity, turnHeading } from '../input/footShaping';
import { chooseDoorSide, entryPath, entryProgress, entryTiming, fromCarFrame, pathLength, pointAlong, toCarFrame, type CarPoint } from './carEntry';

/**
 * Player controller: on-foot movement, the interaction prompt pipeline, entering / stealing /
 * carjacking / exiting vehicles, knockdowns, and driving input.
 * Order 200 (decision band): writes vehicle controls before the vehicle system runs.
 */
/** 'riding' = passenger in someone else's networked car (online only). */
export type PlayerMode = 'foot' | 'entering' | 'driving' | 'exiting' | 'knocked' | 'riding';

export interface PlayerService {
  readonly mode: PlayerMode;
  /** Ground position (vehicle position while driving). */
  readonly pos: V2;
  readonly heading: number;
  readonly velocity: V2;
  /** The on-foot physics body (disabled while driving). */
  readonly body: Body;
  /** Vehicle being driven, entered or exited. */
  readonly vehicle: Vehicle | null;
  /** True while running (for audio/anim). */
  readonly running: boolean;
  /** 0..1 progress of the current enter/exit/break-in action. */
  readonly actionProgress: number;
  /** Vehicle the player would enter if they pressed interact now (for UI prompts). */
  readonly enterCandidate: Vehicle | null;
  /** Getting in: still walking to the door (the clip's approach; the stride follows the real speed). */
  readonly walkingToDoor: boolean;
  /** Running over to a car a few metres off after a press (on foot; any stick push away cancels it). */
  readonly approaching: Vehicle | null;
  enterVehicle(v: Vehicle, instant?: boolean): void;
  exitVehicle(): void;
  teleport(p: V2, heading?: number): void;
  /**
   * Who holds the player's controls (cutscenes, menus, arrests, interiors): each system acquires and
   * releases its own reason; the controls work while none is held (controlLock.ts).
   */
  readonly controls: ControlLock;
  /** True while no reason holds the controls. */
  readonly controlEnabled: boolean;
  /**
   * On-foot stance override (weapons: aiming). `facing` turns the player to that heading instead of
   * the walking direction; `speedScale` slows the walk. `null` = normal movement.
   */
  setStance(s: PlayerStance | null): void;
  /** Break-in time multiplier for locked cars (weapons: a crowbar in hand). */
  setBreakInScale(scale: number): void;
  /**
   * Fall for `seconds`, sliding with `push` (m/s): online the server judged a car ran us over; any blast nearby (fx/explosionFx.ts).
   * Only on foot (no-op otherwise); an existing fall is extended, not restarted.
   */
  knockDown(seconds: number, push?: V2): void;
  /**
   * Online play: route getting in/out of vehicles through an authority (the server). `null` = offline,
   * the player decides locally as in single-player.
   */
  setVehicleAuthority(a: VehicleAuthority | null): void;
  /**
   * The car the game wants the player in (a mission's "get into the red Vesper" stage), or null. Within
   * PREFERRED_REACH it is the get-in target over any nearer car (its prompt shows from there too, and a
   * press runs the player over to it), so a quick tap never steals the parked car next to it instead.
   */
  setPreferredVehicle(v: Vehicle | null): void;
}

export interface PlayerStance {
  facing: number | null;
  speedScale: number;
}

/** Answer to an enter request; the player waits at the door while `state` is 'pending'. */
export interface AuthorityTicket {
  state: 'pending' | 'granted' | 'denied';
  /** Granted seat (0 = driver, the default). A passenger seat makes the player ride along. */
  seat?: number;
}

export interface VehicleAuthority {
  /** False hides the prompt (e.g. another player is driving it). */
  canEnter(v: Vehicle): boolean;
  /** The player committed to entering / stealing `v` (the walk-to-door animation starts now). */
  requestEnter(v: Vehicle): AuthorityTicket;
  /** The player just left `v` and will stand at `spot`. */
  notifyExit(v: Vehicle, spot: V2, heading: number): void;
  /** Override the prompt for `v` (e.g. "Ride along"); null = the default. */
  prompt?(v: Vehicle): string | null;
}

declare module '../core/services' {
  interface ServiceMap {
    player: PlayerService;
  }
}

export { JOG_SPEED, RUN_SPEED } from '../input/footShaping';
/** Max distance from the car's body edge to start entering (and to show the prompt). */
const ENTER_REACH = 2.4;
/** A press with no car in reach runs the player over to one this close (edge distance, m). */
const APPROACH_REACH = 6;
/** Give up running over after this long (s), or after this long without getting closer. */
const APPROACH_MAX = 2.2;
const APPROACH_STUCK = 0.45;
/** A mission's car (setPreferredVehicle) is the get-in target this close (edge distance, m). */
const PREFERRED_REACH = 12;
/** A get-in press with nothing in reach is remembered this long (s): pressing a moment early still works. */
const PRESS_BUFFER = 0.3;
const ENTER_TIME = 0.5;
const CARJACK_TIME = 0.95;
const BREAK_IN_TIME = 1.4;
const EXIT_TIME = 0.4;
/** Moving AI cars can be jacked only when slower than this (m/s). */
const CARJACK_MAX_SPEED = 3.5;

export function createPlayerSystem(): System {
  let ctx: GameContext;
  let vehicles: VehicleService;
  let rig: CameraRig | undefined;
  let body: Body;
  let mode: PlayerMode = 'foot';
  let heading = 0;
  let vehicle: Vehicle | null = null;
  let actionTimer = 0;
  let actionDuration = 0;
  let actionFrom: V2 = { x: 0, z: 0 };
  let actionTo: V2 = { x: 0, z: 0 };
  let carjacking = false;
  // Getting in (docs/CONTROLS-POLISH.md): the door the player goes to and the path round the bodywork, in the
  // car's own frame so they follow a car that is still rolling.
  let doorSide: 1 | -1 = 1;
  let entryFrom: CarPoint = { lat: 0, al: 0 };
  let entryWay: CarPoint[] = [];
  let entryTimes = { walk: 0, door: 0 };
  let approachCar: Vehicle | null = null;
  let approachTimer = 0;
  let approachBest = Infinity;
  let approachStall = 0;
  let pressBuffered = 0;
  let preferred: Vehicle | null = null;
  let running = false;
  const controls = new ControlLock();
  let candidate: Vehicle | null = null;
  let knockTimer = 0;
  let authority: VehicleAuthority | null = null;
  let ticket: AuthorityTicket | null = null;
  let stance: PlayerStance | null = null;
  let breakInScale = 1;
  const focusPose = { x: 0, z: 0, angle: 0 };
  // Driving input shaping (docs/DRIVING.md §8): keys ramp with speed, a pad stick gets a curve, the
  // touch stick is smoothed. Settings → Steering sensitivity / Driving assists.
  const shaper = new DriveShaper();
  let shape: DriveShapeOptions = { ...DEFAULT_DRIVE_SHAPE };
  const interactables: Interactable[] = [];
  let current: Interactable | null = null;
  let currentPrompt: InteractionPrompt | null = null;
  let builtInPrompt = true;
  let promptEl: HTMLDivElement | null = null;
  const promptVec = new Vector3();

  /** A door in the car's frame. UK cars are right-hand drive, so the driver's door (1) is on the right. */
  function doorLocal(v: Vehicle, side: 1 | -1 = 1): CarPoint {
    const along = v.spec.body === 'lorry' ? v.spec.length / 2 - 1.4 : v.spec.wheelbase * 0.12;
    return { lat: (v.spec.width / 2 + 0.55) * side, al: along };
  }

  function doorPos(v: Vehicle, side: 1 | -1 = 1): V2 {
    return fromCarFrame(doorLocal(v, side), v.pos, v.body.right(), v.body.forward());
  }

  function carFrame(v: Vehicle, p: V2): CarPoint {
    return toCarFrame(p, v.pos, v.body.right(), v.body.forward());
  }

  /** Distance from p to the vehicle's collision box edge. */
  function edgeDistance(v: Vehicle, p: V2): number {
    const l = v.body.worldToLocal(p);
    const dx = Math.max(Math.abs(l.x) - v.spec.width / 2, 0);
    const dz = Math.max(Math.abs(l.z) - v.spec.length / 2, 0);
    return Math.hypot(dx, dz);
  }

  function canEnter(v: Vehicle): boolean {
    if (v.destroyed) return false;
    if (v.occupant?.kind === 'player') return false;
    if (authority && !authority.canEnter(v)) return false;
    if (v.occupant?.kind === 'ped' && v.speed > CARJACK_MAX_SPEED) return false;
    return true;
  }

  function findCandidate(reach = ENTER_REACH): Vehicle | null {
    // The mission's car wins over a nearer one while it is close (setPreferredVehicle).
    if (preferred && !preferred.destroyed && canEnter(preferred) && edgeDistance(preferred, body.pos) <= Math.max(reach, PREFERRED_REACH)) return preferred;
    let best: Vehicle | null = null;
    let bestD = reach;
    for (const v of vehicles.query(body.pos, reach + 8)) {
      if (!canEnter(v)) continue;
      const d = edgeDistance(v, body.pos);
      if (d < bestD) {
        bestD = d;
        best = v;
      }
    }
    return best;
  }

  function vehiclePrompt(v: Vehicle): string {
    const name = v.spec.displayName;
    const custom = authority?.prompt?.(v);
    if (custom) return custom;
    if (v.occupant?.kind === 'ped') return v.isPolice ? `Pull the copper out` : `Pull the driver out`;
    if (v.ownedByPlayer || v.stolen) return `Get in the ${name}`;
    if (v.isPolice) return `Nick the ${name}`;
    if (v.locked) return `Break into the ${name}`;
    return `Steal the ${name}`;
  }

  function beginEnter(v: Vehicle, instant: boolean): void {
    vehicle = v;
    mode = 'entering';
    actionTimer = 0;
    approachCar = null;
    pressBuffered = 0;
    carjacking = v.occupant?.kind === 'ped';
    const base = instant ? 0 : carjacking ? CARJACK_TIME : v.locked && !v.ownedByPlayer && !v.stolen ? BREAK_IN_TIME * breakInScale : ENTER_TIME;
    actionFrom = { x: body.pos.x, z: body.pos.z };
    // The nearer door (in through the passenger side and slide across), reached round the bodywork.
    entryFrom = carFrame(v, body.pos);
    doorSide = chooseDoorSide(entryFrom.lat);
    entryWay = entryPath(entryFrom, doorLocal(v, doorSide), v.spec);
    entryTimes = entryTiming(base, pathLength(entryFrom, entryWay));
    actionDuration = entryTimes.walk + entryTimes.door;
    body.setVelocity({ x: 0, z: 0 });
    ticket = authority ? authority.requestEnter(v) : null;
    if (carjacking) {
      // Take the car away from its AI so it stops while we drag the driver out.
      v.controller = 'none';
      v.controls.throttle = 0;
      v.controls.steer = 0;
      v.controls.handbrake = true;
    }
    if (instant && !ticket) completeEnter();
  }

  function completeEnter(): void {
    const v = vehicle;
    if (!v) return;
    const seat = ticket?.seat ?? 0;
    ticket = null;
    if (seat > 0) {
      // Passenger: the car is someone else's to drive; we just sit in it (net module keeps us in step).
      body.enabled = false;
      mode = 'riding';
      return;
    }
    const prev = v.occupant;
    const carjack = prev !== null && prev.kind === 'ped';
    const pos = { x: v.pos.x, z: v.pos.z };
    if (carjack) {
      // The driver is shoved out of the other door, away from the player.
      ctx.events.emit('driverEjected', { vehicleId: v.id, pedId: prev.kind === 'ped' ? prev.pedId : null, pos: doorPos(v, doorSide === 1 ? -1 : 1), heading: v.heading + (Math.PI / 2) * doorSide });
    }
    const wasLocked = v.locked;
    v.occupant = { kind: 'player' };
    v.controller = 'player';
    v.locked = false;
    v.controls.handbrake = false;
    v.headlights = true;
    v.body.wake();
    body.enabled = false;
    mode = 'driving';
    ctx.events.emit('vehicleEntered', { vehicleId: v.id, byPlayer: true });
    if (!v.ownedByPlayer && (!v.stolen || carjack)) {
      v.stolen = true;
      ctx.events.emit('vehicleStolen', { vehicleId: v.id, byPlayer: true, pos, carjack, isPolice: v.isPolice });
      const type: CrimeType = v.isPolice ? 'policeCarTheft' : carjack ? 'carjack' : 'carTheft';
      const severity = v.isPolice ? 4 : carjack ? 3 : wasLocked ? 2 : 1;
      ctx.events.emit('crime', { type, pos, severity, byPlayer: true, vehicleId: v.id });
    }
  }

  function exitSpot(v: Vehicle): V2 {
    const f = v.body.forward();
    const candidates: V2[] = [
      doorPos(v, 1),
      doorPos(v, -1),
      { x: v.pos.x - f.x * (v.spec.length / 2 + 0.8), z: v.pos.z - f.z * (v.spec.length / 2 + 0.8) },
      { x: v.pos.x + f.x * (v.spec.length / 2 + 0.8), z: v.pos.z + f.z * (v.spec.length / 2 + 0.8) },
    ];
    for (const c of candidates) {
      if (ctx.physics.isShapeFree(body.shape, c, 0, Layer.VEHICLE | Layer.PROP | Layer.PED, v.body)) return c;
    }
    // Boxed in: try further out in a ring.
    for (let r = 2; r <= 5; r += 1) {
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2;
        const c = { x: v.pos.x + Math.cos(a) * (v.spec.width / 2 + r), z: v.pos.z + Math.sin(a) * (v.spec.length / 2 + r) };
        if (ctx.physics.isShapeFree(body.shape, c, 0, Layer.ALL, v.body)) return c;
      }
    }
    return candidates[0]!;
  }

  function exitVehicle(): void {
    const v = vehicle;
    if (!v || (mode !== 'driving' && mode !== 'riding')) return;
    if (mode === 'driving') {
      v.occupant = null;
      v.controller = 'none';
      v.controls.throttle = 0;
      v.controls.steer = 0;
    }
    const spot = exitSpot(v);
    const speed = v.speed;
    heading = v.heading;
    authority?.notifyExit(v, spot, heading);
    ctx.events.emit('vehicleExited', { vehicleId: v.id, byPlayer: true });
    if (speed > 8) {
      // Bail out at speed: tumble onto the road.
      body.enabled = true;
      body.setPose(spot, 0);
      body.setVelocity({ x: v.velocity.x * 0.45, z: v.velocity.z * 0.45 }, 0);
      vehicle = null;
      knockDown(speed > 16 ? 1.4 : 0.9);
      if (speed > 16) ctx.events.emit('playerHurt', { amount: Math.round(speed), cause: 'bailOut', pos: { x: spot.x, z: spot.z } });
      return;
    }
    // Step out: start at the seat, walk to the door spot.
    mode = 'exiting';
    actionTimer = 0;
    actionDuration = EXIT_TIME;
    actionFrom = v.body.localToWorld({ x: -v.spec.width * 0.2, z: 0.2 });
    actionTo = spot;
    body.enabled = true;
    body.layer = Layer.DEBRIS; // don't collide with our own car while stepping out
    body.mask = Layer.STATIC;
    body.setPose(actionFrom, 0);
    body.setVelocity({ x: 0, z: 0 }, 0);
  }

  function knockDown(seconds: number): void {
    approachCar = null;
    pressBuffered = 0;
    mode = 'knocked';
    knockTimer = seconds;
    vehicle = null;
    candidate = null;
  }

  function teleport(p: V2, h?: number): void {
    if (mode === 'driving' && vehicle) {
      vehicle.body.setPose(p, h ?? vehicle.heading);
      vehicle.body.setVelocity({ x: 0, z: 0 }, 0);
    } else {
      if (mode !== 'foot' && mode !== 'knocked') {
        if (vehicle && mode === 'entering' && carjacking) vehicle.controller = 'ai';
        vehicle = null;
        ticket = null;
      }
      mode = 'foot';
      approachCar = null;
      pressBuffered = 0;
      restoreFootBody();
      body.setPose(p, 0);
      body.setVelocity({ x: 0, z: 0 }, 0);
      if (h !== undefined) heading = h;
    }
    ctx.events.emit('playerTeleported', { pos: { x: p.x, z: p.z } });
  }

  /** Get in: straight away within reach, else run over first (a mission's car from further off). */
  function getIn(v: Vehicle): void {
    if (edgeDistance(v, body.pos) <= ENTER_REACH) beginEnter(v, false);
    else startApproach(v);
  }

  function startApproach(v: Vehicle): void {
    approachCar = v;
    approachTimer = 0;
    approachBest = Infinity;
    approachStall = 0;
    pressBuffered = 0;
  }

  /**
   * One step of running over to `v`: the direction to go (the next point of the path round the car), or
   * null when it is over: in reach (the enter begins), the car went or can't be had, a stick push away
   * from it, too long, or stuck on something.
   */
  function approachStep(v: Vehicle, dt: number, stickX: number, stickZ: number, stickMag: number): V2 | null {
    approachTimer += dt;
    const d = edgeDistance(v, body.pos);
    if (!canEnter(v) || approachTimer > APPROACH_MAX) {
      approachCar = null;
      return null;
    }
    if (d <= ENTER_REACH * 0.6) {
      beginEnter(v, false);
      return null;
    }
    if (d < approachBest - 0.05) {
      approachBest = d;
      approachStall = 0;
    } else if ((approachStall += dt) > APPROACH_STUCK) {
      approachCar = null;
      return null;
    }
    const from = carFrame(v, body.pos);
    const way = entryPath(from, doorLocal(v, chooseDoorSide(from.lat)), v.spec);
    const next = fromCarFrame(way[0]!, v.pos, v.body.right(), v.body.forward());
    const dx = next.x - body.pos.x;
    const dz = next.z - body.pos.z;
    const l = Math.hypot(dx, dz) || 1;
    // The thumb pushing away from the car (more than a right angle off): the player wants to go elsewhere.
    if (stickMag > 0.5 && (stickX * dx + stickZ * dz) / (l * stickMag) < 0) {
      approachCar = null;
      return null;
    }
    return { x: dx / l, z: dz / l };
  }

  function restoreFootBody(): void {
    body.enabled = true;
    body.layer = Layer.PLAYER;
    body.mask = Layer.ALL;
  }

  const interactionService: InteractionService = {
    register(i) {
      interactables.push(i);
      return () => {
        const k = interactables.indexOf(i);
        if (k >= 0) interactables.splice(k, 1);
      };
    },
    get current() {
      return currentPrompt;
    },
    setBuiltInPrompt(enabled) {
      builtInPrompt = enabled;
      if (!enabled && promptEl) promptEl.style.display = 'none';
    },
  };

  const service: PlayerService = {
    get mode() {
      return mode;
    },
    get pos() {
      return (mode === 'driving' || mode === 'riding') && vehicle ? vehicle.pos : body.pos;
    },
    get heading() {
      return (mode === 'driving' || mode === 'riding') && vehicle ? vehicle.heading : heading;
    },
    get velocity() {
      return (mode === 'driving' || mode === 'riding') && vehicle ? vehicle.velocity : body.vel;
    },
    get body() {
      return body;
    },
    get vehicle() {
      return vehicle;
    },
    get running() {
      return running;
    },
    get actionProgress() {
      if (mode === 'entering') return entryProgress(actionTimer, entryTimes);
      return mode === 'exiting' ? clamp(actionTimer / Math.max(actionDuration, 1e-3), 0, 1) : 0;
    },
    get enterCandidate() {
      return candidate;
    },
    get walkingToDoor() {
      return mode === 'entering' && actionTimer < entryTimes.walk;
    },
    get approaching() {
      return approachCar;
    },
    enterVehicle: (v, instant = false) => {
      if (mode === 'driving' || mode === 'riding') exitVehicle();
      if (mode === 'exiting' || mode === 'knocked') restoreFootBody();
      beginEnter(v, instant);
    },
    exitVehicle,
    teleport,
    controls,
    get controlEnabled() {
      return controls.enabled;
    },
    setStance(s) {
      stance = s;
    },
    setBreakInScale(scale) {
      breakInScale = clamp(scale, 0.1, 1);
    },
    knockDown(seconds, push) {
      if (mode === 'knocked') {
        knockTimer = Math.max(knockTimer, seconds);
        return;
      }
      if (mode !== 'foot') return;
      knockDown(seconds);
      if (push) body.setVelocity({ x: push.x, z: push.z }, 0);
    },
    setVehicleAuthority(a) {
      authority = a;
      ticket = null;
    },
    setPreferredVehicle(v) {
      preferred = v;
    },
  };

  function updatePromptEl(): void {
    if (!ctx.uiRoot) return;
    if (!promptEl) {
      promptEl = document.createElement('div');
      promptEl.id = 'interact-prompt';
      promptEl.style.cssText =
        'position:absolute;transform:translate(-50%,-100%);pointer-events:none;white-space:nowrap;display:none;' +
        "font:600 14px/1 'Arial Narrow','Helvetica Neue',Arial,sans-serif;letter-spacing:.06em;text-transform:uppercase;" +
        'color:#efe6cf;background:rgba(14,12,10,.86);padding:6px 10px 6px 6px;border-left:3px solid #d9a441;' +
        'box-shadow:0 2px 0 rgba(0,0,0,.5);z-index:50;';
      ctx.uiRoot.appendChild(promptEl);
    }
    if (!builtInPrompt || !currentPrompt || mode !== 'foot' || !ctx.render) {
      promptEl.style.display = 'none';
      return;
    }
    const vec = promptVec.set(currentPrompt.pos.x, 2.1, currentPrompt.pos.z).project(ctx.camera);
    const w = ctx.render.canvas.clientWidth;
    const h = ctx.render.canvas.clientHeight;
    promptEl.style.display = 'block';
    promptEl.style.left = `${((vec.x + 1) / 2) * w}px`;
    promptEl.style.top = `${((1 - vec.y) / 2) * h}px`;
    const pad = ctx.input.lastDevice === 'gamepad';
    const chip = (k: string): string => `<span style="display:inline-block;background:#d9a441;color:#14110d;padding:3px 6px;margin-right:8px;font-weight:800">${k}</span>`;
    // A place and a car both in reach: E / USE is the place, F gets in the car.
    const car = candidate && !currentPrompt.id.startsWith('vehicle:') ? `<div style="margin-top:4px">${chip(pad ? 'B' : 'F')}${vehiclePrompt(candidate)}</div>` : '';
    promptEl.innerHTML = `${chip(pad ? 'Y' : 'E')}${currentPrompt.prompt}${car}`;
  }

  return {
    id: 'player',
    order: 200,
    init(c) {
      ctx = c;
      const layout = c.services.tryGet('layout');
      const spawn = layout?.playerSpawn ?? { pos: { x: 0, z: 0 }, heading: 0 };
      heading = spawn.heading;
      body = c.physics.createBody({
        shape: { type: 'circle', r: 0.35 },
        pos: spawn.pos,
        mass: 80,
        restitution: 0,
        friction: 0.2,
        layer: Layer.PLAYER,
        mask: Layer.ALL,
        kind: 'player',
        entityId: 0,
        canSleep: false,
      });
      c.services.provide('player', service);
      c.services.provide('interactions', interactionService);

      c.hooks.register('teleport', (x, z) => teleport({ x, z }));
      c.hooks.register('enterVehicle', (id) => {
        const v = vehicles.get(id);
        if (!v) throw new Error(`No vehicle ${id}`);
        service.enterVehicle(v, true);
      });
      c.hooks.register('exitVehicle', () => exitVehicle());
      c.hooks.registerSnapshot('player', () => ({
        mode,
        x: +service.pos.x.toFixed(2),
        z: +service.pos.z.toFixed(2),
        heading: +service.heading.toFixed(3),
        speed: +Math.hypot(service.velocity.x, service.velocity.z).toFixed(2),
        vehicleId: vehicle?.id ?? null,
        prompt: currentPrompt?.prompt ?? null,
        approaching: approachCar?.id ?? null,
        controlLock: controls.reasons,
      }));
      c.hooks.register('controlLock', () => controls.reasons);

      // Control-lock safety nets: a respawn ends the arrest / death and whatever job was going on; a new
      // game starts with nothing holding the controls.
      c.events.on('playerRespawned', () => controls.drop(...RESPAWN_REASONS));
      c.events.on('newGameStarted', () => controls.clear());

      // Getting hit by a car knocks the player down.
      c.events.on('collision', (e) => {
        if (mode !== 'foot' && mode !== 'entering') return;
        // Online, getting in is the server's: once it has the request (a break-in takes 2.5 s at the door), a car
        // only this client simulates (ambient traffic) must not knock us off it — the server would seat us anyway
        // and we would be left on foot beside a car we are driving (the e2e "press at the door" failure). Being
        // run over by a networked car still comes from the server (`knock`).
        if (mode === 'entering' && ticket) return;
        const isPlayer = e.a.kind === 'player' || e.b?.kind === 'player';
        const other = e.a.kind === 'player' ? e.b : e.a;
        if (!isPlayer || !other || other.kind !== 'vehicle' || e.speed < 5) return;
        // Only a MOVING car knocks you down; running into a parked one doesn't (playtest DRIVING-6).
        const hitter = vehicles.get(other.id);
        if (!hitter || Math.abs(hitter.velocity.x * e.normal.x + hitter.velocity.z * e.normal.z) < 4) return;
        if (mode === 'entering' && vehicle && carjacking) vehicle.controller = 'ai';
        knockDown(Math.min(2.2, 0.6 + e.speed / 12));
        ctx.events.emit('playerHurt', { amount: Math.round(e.speed * 2), cause: 'hitByVehicle', pos: { x: body.pos.x, z: body.pos.z } });
      });
    },
    start() {
      vehicles = ctx.services.get('vehicles');
      rig = ctx.services.tryGet('cameraRig');
      const applyShape = (s: { steerSensitivity: number; driveAssists: boolean }): void => {
        shape = { sensitivity: s.steerSensitivity, assists: s.driveAssists };
      };
      const save = ctx.services.tryGet('save');
      if (save) applyShape(save.settings);
      ctx.events.on('settingsChanged', (e) => applyShape(e.settings));
    },
    fixedUpdate(dt) {
      const input = ctx.input;
      const active = controls.enabled && ctx.state.mode !== 'cutscene';

      if (mode === 'foot') {
        candidate = findCandidate();
        // Interaction target: registered interactables beat vehicles when they have higher priority.
        const vehicleIt: Interactable | null = candidate
          ? { id: `vehicle:${candidate.id}`, pos: () => candidate!.pos, radius: Infinity, priority: 10, prompt: () => vehiclePrompt(candidate!), onInteract: () => getIn(candidate!) }
          : null;
        current = pickInteractable(vehicleIt ? [...interactables, vehicleIt] : interactables, body.pos, false);
        currentPrompt = current ? { id: current.id, prompt: current.prompt(), pos: current.pos() } : null;

        let mx = active ? input.axis('moveX') : 0;
        let my = active ? input.axis('moveY') : 0;
        let mag = Math.hypot(mx, my);
        if (mag > 1) {
          mx /= mag;
          my /= mag;
        }
        const up = rig ? rig.screenUp() : { x: 0, z: -1 };
        const rt = rig ? rig.screenRight() : { x: 1, z: 0 };
        let dirX = up.x * my + rt.x * mx;
        let dirZ = up.z * my + rt.z * mx;
        // Running over to a car after a press: the trot is steered for the player until a stick push away.
        const goal = approachCar && active ? approachStep(approachCar, dt, dirX, dirZ, mag) : null;
        if (goal) {
          dirX = goal.x;
          dirZ = goal.z;
          mag = 1;
        }
        // Sprinting is RUN only (held: Shift, the pad button, the touch knob) — the stick's deflection gives a
        // stepless walk → jog, and with RUN any push past the dead zone is the full sprint (footShaping.ts).
        running = active && (input.held('run') || !!goal) && mag > MOVE_DEAD;
        const speed = footTargetSpeed(mag, running) * (stance && !goal ? stance.speedScale : 1);
        // `dir` carries the deflection; the target speed is along its unit direction.
        const ux = mag > MOVE_DEAD ? dirX / mag : 0;
        const uz = mag > MOVE_DEAD ? dirZ / mag : 0;
        const tx = ux * speed;
        const tz = uz * speed;
        const rate = footSlewRate(mag, running, body.vel.x, body.vel.z, tx, tz);
        body.setVelocity(slewVelocity(body.vel.x, body.vel.z, tx, tz, rate, dt), 0);
        if (stance && stance.facing !== null && !goal) {
          // Aiming: face the target (or keep the line) while strafing — damped, so a lock taking or
          // switching turns the body rather than snapping it (footShaping.ts).
          heading = aimTurnHeading(heading, stance.facing, dt);
        } else if (mag > MOVE_DEAD) {
          // Damped turn towards the push: a light push turns gently, so stick noise does not shake the body.
          heading = turnHeading(heading, Math.atan2(ux, uz), mag, dt);
        }
        body.angle = 0;
        // F / the CAR knob: the car, even with a phone box or a door in reach (E / USE takes those first).
        // No car in reach: run over to one a few metres off; with none about, the press is kept a moment
        // (PRESS_BUFFER) so pressing just before reaching a car still gets in.
        const pressedCar = active && input.pressed('vehicle');
        const pressedUse = active && input.pressed('enterExit');
        if (approachCar && active && (input.pressed('attack') || input.pressed('aim'))) approachCar = null;
        if (pressedUse && current && !current.id.startsWith('vehicle:')) {
          pressBuffered = 0;
          current.onInteract();
        } else if (mode === 'foot' && active && (pressedCar || pressedUse || pressBuffered > 0)) {
          if (candidate) getIn(candidate);
          else if (pressedCar || pressedUse) {
            const far = approachCar ? null : findCandidate(APPROACH_REACH);
            if (far) startApproach(far);
            else if (!approachCar) pressBuffered = PRESS_BUFFER;
          }
        }
        pressBuffered = Math.max(0, pressBuffered - dt);
      } else if (mode === 'entering') {
        const v = vehicle;
        candidate = null;
        currentPrompt = null;
        if (!v || v.destroyed || (carjacking && v.speed > CARJACK_MAX_SPEED + 3) || ticket?.state === 'denied') {
          if (v && carjacking && !v.destroyed) v.controller = 'ai';
          mode = 'foot';
          vehicle = null;
          ticket = null;
          return;
        }
        actionTimer += dt;
        const door = doorPos(v, doorSide);
        // Along the path round the bodywork at a steady trot, in the car's frame (it may still be rolling).
        const walk = entryTimes.walk > 0 ? Math.min(1, actionTimer / entryTimes.walk) : 1;
        const at = fromCarFrame(pointAlong(entryFrom, entryWay, walk), v.pos, v.body.right(), v.body.forward());
        const stepX = at.x - body.pos.x;
        const stepZ = at.z - body.pos.z;
        body.setPose(at, 0, false);
        body.setVelocity({ x: 0, z: 0 }, 0);
        // Face the way we walk, then the car once at the door.
        const face = walk < 1 && Math.hypot(stepX, stepZ) > 1e-3 ? Math.atan2(stepX, stepZ) : Math.atan2(v.pos.x - body.pos.x, v.pos.z - body.pos.z);
        heading += angleDelta(heading, face) * (walk < 1 ? 0.35 : 0.3);
        // Cancel by walking away early (while still walking over, or just at the door).
        const t = clamp(actionTimer / Math.max(actionDuration, 1e-3), 0, 1);
        if (!ticket && active && Math.hypot(input.axis('moveX'), input.axis('moveY')) > 0.5 && actionTimer > 0.15 && (walk < 1 || t < 0.5) && distance(body.pos, door) > 0.2) {
          if (carjacking) v.controller = 'ai';
          mode = 'foot';
          vehicle = null;
          return;
        }
        // Online: wait at the door until the server has said yes (it answers in about one round trip).
        if (actionTimer >= actionDuration && (!ticket || ticket.state === 'granted')) completeEnter();
      } else if (mode === 'exiting') {
        actionTimer += dt;
        const t = clamp(actionTimer / EXIT_TIME, 0, 1);
        const e = t * t * (3 - 2 * t);
        body.setPose({ x: actionFrom.x + (actionTo.x - actionFrom.x) * e, z: actionFrom.z + (actionTo.z - actionFrom.z) * e }, 0, false);
        body.setVelocity({ x: 0, z: 0 }, 0);
        if (t >= 1) {
          restoreFootBody();
          vehicle = null;
          mode = 'foot';
        }
      } else if (mode === 'knocked') {
        knockTimer -= dt;
        running = false;
        currentPrompt = null;
        // Slide to a stop.
        body.setVelocity({ x: body.vel.x * (1 - Math.min(1, dt * 4)), z: body.vel.z * (1 - Math.min(1, dt * 4)) }, 0);
        restoreFootBody();
        if (knockTimer <= 0) mode = 'foot';
      } else if (mode === 'riding') {
        const v = vehicle;
        candidate = null;
        currentPrompt = null;
        if (!v || v.destroyed) {
          body.enabled = true;
          mode = 'foot';
          vehicle = null;
          return;
        }
        // Ride along: the body shadows the car so the camera, minimap and heat checks follow it.
        body.setPose(v.pos, 0, false);
        body.setVelocity({ x: 0, z: 0 }, 0);
        heading = v.heading;
        if (active && (input.pressed('enterExit') || input.pressed('vehicle'))) exitVehicle();
      } else if (mode === 'driving') {
        const v = vehicle;
        candidate = null;
        if (!v) {
          mode = 'foot';
          return;
        }
        const drivingIt = pickInteractable(interactables, v.pos, true);
        currentPrompt = drivingIt ? { id: drivingIt.id, prompt: drivingIt.prompt(), pos: drivingIt.pos() } : null;
        if (active) {
          v.controls.throttle = input.axis('throttle');
          v.controls.steer = shaper.steer(input.axis('steer'), input.axisSource('steer'), v.speed, dt, shape);
          v.controls.handbrake = input.held('handbrake');
          if (input.pressed('vehicle')) exitVehicle();
          else if (input.pressed('enterExit')) {
            if (drivingIt) drivingIt.onInteract();
            else exitVehicle();
          }
        } else {
          v.controls.throttle = 0;
          v.controls.steer = 0;
          v.controls.handbrake = true;
          shaper.reset();
        }
        // Burning car: the player bails automatically just before it blows.
        if (v.condition === 'burning' && v.fireTimer < 1.2 && mode === 'driving') exitVehicle();
      }
    },
    update(_dt, alpha) {
      const r = rig ?? ctx.services.tryGet('cameraRig');
      if (r) {
        const b = (mode === 'driving' || mode === 'riding') && vehicle ? vehicle.body : body;
        b.interpolate(alpha, focusPose);
        r.setFocus({ pos: { x: focusPose.x, z: focusPose.z }, vel: service.velocity, inVehicle: mode === 'driving' || mode === 'riding' });
      }
      updatePromptEl();
    },
    dispose() {
      promptEl?.remove();
    },
  };
}
